import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Calendar, Clock, Phone, HelpCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { getMenteeBookings, SessionBookingWithMentor } from '@/utils/menteeBookingsUtils';
import { format, parseISO, isAfter, startOfDay } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { useDailyVideoCall } from '@/hooks/useDailyVideoCall';
import { useCallDurationManager } from '@/hooks/useCallDurationManager';
import { useToast } from '@/hooks/use-toast';
import { getUserTimezone, convertFromUTC, getTimezoneAbbreviation } from '@/utils/timezoneUtils';

const UpcomingAppointments = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState<SessionBookingWithMentor[]>([]);
  const [loading, setLoading] = useState(true);
  const [courseTypes, setCourseTypes] = useState<Record<string, string>>({});
  const [callInProgress, setCallInProgress] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userTimezone] = useState<string>(getUserTimezone());
  const { findOrCreateVideoRoom, startVideoCall, isCreatingRoom } = useDailyVideoCall();
  const { isCallActive, callDuration, startCallTimer, endCallTimer } = useCallDurationManager();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      loadUpcomingBookings();
    }
  }, [user]);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Add event listener for booking-completed events
  useEffect(() => {
    const handleBookingCompleted = (event: CustomEvent) => {
      console.log('DEBUG: UpcomingAppointments - Received booking-completed event:', event.detail);
      // Refresh the upcoming appointments list
      loadUpcomingBookings();
    };

    // Add event listener
    window.addEventListener('booking-completed', handleBookingCompleted as EventListener);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('booking-completed', handleBookingCompleted as EventListener);
    };
  }, []);

  // Add event listener for subscription-ended events
  useEffect(() => {
    const handleSubscriptionEnded = (event: CustomEvent) => {
      console.log('DEBUG: UpcomingAppointments - Received subscription-ended event:', event.detail);
      // Refresh the upcoming appointments list to filter out ended subscription bookings
      loadUpcomingBookings();
    };

    // Add event listener
    window.addEventListener('subscription-ended', handleSubscriptionEnded as EventListener);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('subscription-ended', handleSubscriptionEnded as EventListener);
    };
  }, []);

  const loadUpcomingBookings = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      console.log('DEBUG: UpcomingAppointments - Loading upcoming bookings...');
      
      // First, update any bookings that should be completed
      await updateCompletedBookings();
      
      const result = await getMenteeBookings(user.id);
      if (result.success && result.data) {
        // Filter for upcoming bookings with subscription status check
        const upcomingBookings = result.data.filter(booking => {
          const bookingDate = parseISO(booking.booked_date);
          const today = startOfDay(new Date());
          
          // Existing date and status checks
          const isUpcoming = (isAfter(bookingDate, today) || format(bookingDate, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')) 
                             && (booking.status === 'confirmed' || booking.status === 'pending');
          
          // New subscription status check
          const isValidSubscription = booking.course_type !== 'subscription' || 
            (booking.is_subscription_active && 
             (!booking.subscription_end_date || new Date(booking.subscription_end_date) > new Date()));
          
          console.log('DEBUG: Filtering booking', {
            bookingId: booking.id,
            courseType: booking.course_type,
            isSubscriptionActive: booking.is_subscription_active,
            subscriptionEndDate: booking.subscription_end_date,
            isUpcoming,
            isValidSubscription
          });
          
          return isUpcoming && isValidSubscription;
        }).sort((a, b) => new Date(a.booked_date).getTime() - new Date(b.booked_date).getTime());
        
        console.log('DEBUG: UpcomingAppointments - Loaded bookings:', upcomingBookings.length);
        setBookings(upcomingBookings);
        
        // Fetch course types for each booking
        if (upcomingBookings.length > 0) {
          fetchCourseTypes(upcomingBookings);
        }
      }
    } catch (error) {
      console.error('Error loading upcoming bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateCompletedBookings = async () => {
    try {
      const { error } = await supabase.rpc('update_voicevideo_bookings_to_completed');
      if (error) {
        console.error('Error updating completed bookings:', error);
      }
    } catch (error) {
      console.error('Error calling update function:', error);
    }
  };

  const fetchCourseTypes = async (bookings: SessionBookingWithMentor[]) => {
    const newCourseTypes: Record<string, string> = {};
    
    for (const booking of bookings) {
      if (booking.application_id) {
        try {
          const { data, error } = await supabase
            .from('applications')
            .select(`
              courses (
                course_type
              )
            `)
            .eq('id', booking.application_id)
            .single();

          if (error) {
            console.error('Error fetching course type for booking:', booking.id, error);
            continue;
          }

          if (data?.courses) {
            newCourseTypes[booking.id] = data.courses.course_type;
          }
        } catch (error) {
          console.error('Error in fetchCourseType for booking:', booking.id, error);
        }
      }
    }
    
    setCourseTypes(newCourseTypes);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCourseTypeDisplay = (courseType: string, subscriptionType?: string) => {
    if (courseType === 'subscription') {
      return subscriptionType === 'monthly' ? 'Monthly Mentoring' : 'Weekly Mentoring';
    }
    return courseType || 'Voice/Video Call';
  };

  const getCourseTypeColor = (courseType: string) => {
    switch (courseType) {
      case 'subscription': return 'bg-blue-100 text-blue-800';
      case 'Voice/Video Call': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleStartCall = async (booking: SessionBookingWithMentor) => {
    try {
      setCallInProgress(true);
      
      console.log('Starting video call for booking:', booking.id);
      
      // Find or create Daily.co room using the shared room logic
      const sessionData = await findOrCreateVideoRoom({
        mentorId: booking.mentor_id,
        menteeId: booking.mentee_id,
        bookingId: booking.id
      });

      if (sessionData) {
        // Start the call duration timer
        await startCallTimer(booking.mentor_id, booking.mentee_id, booking.booked_date);
        
        // Open the video call
        startVideoCall(sessionData.room_url, () => {
          console.log('Call started, duration tracking active');
        });
      }
    } catch (error) {
      console.error('Error starting video call:', error);
      toast({
        title: "Error",
        description: "Failed to start video call. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCallInProgress(false);
    }
  };

  // Check if call button should be enabled based on time
  const getCallButtonState = (booking: SessionBookingWithMentor) => {
    if (booking.status !== 'confirmed') {
      return { enabled: false, tooltip: 'Appointment must be confirmed to start call' };
    }

    try {
      // Convert UTC booking times to local timezone
      const startLocal = convertFromUTC(
        booking.booked_date,
        booking.start_time,
        userTimezone
      );

      const endLocal = convertFromUTC(
        booking.booked_date,
        booking.end_time,
        userTimezone
      );

      const now = currentTime;

      if (now < startLocal.localDate) {
        return { 
          enabled: false, 
          tooltip: `The call will be available at ${startLocal.localTime}` 
        };
      } else if (now > endLocal.localDate) {
        return { 
          enabled: false, 
          tooltip: `This call ended at ${endLocal.localTime}` 
        };
      } else {
        return { 
          enabled: true, 
          tooltip: 'Click to start your scheduled call' 
        };
      }
    } catch (error) {
      console.error('Error parsing appointment times:', error);
      return { enabled: false, tooltip: 'Invalid appointment time format' };
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
        <p className="text-gray-600 mb-2">No appointments scheduled.</p>
        <p className="text-sm text-gray-500">
          Your confirmed and pending appointments will appear here.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {bookings.slice(0, 5).map((booking) => (
          <Card key={booking.id} className="p-4">
            <CardContent className="p-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={booking.mentor.avatar_url} />
                    <AvatarFallback>
                      {booking.mentor.first_name[0]}{booking.mentor.last_name[0]}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {booking.mentor.first_name} {booking.mentor.last_name}
                    </h3>
                    {booking.application_id && (
                      <p className="text-xs text-muted-foreground">App ID: {booking.application_id}</p>
                    )}
                    {(() => {
                      // Convert UTC booking time to user's local timezone
                      const bookingLocal = convertFromUTC(
                        booking.booked_date,
                        booking.start_time,
                        userTimezone
                      );

                      const endLocal = convertFromUTC(
                        booking.booked_date,
                        booking.end_time,
                        userTimezone
                      );

                      return (
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {format(bookingLocal.localDate, 'EEE, MMM d')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {bookingLocal.localTime} - {endLocal.localTime}
                            <span className="text-xs text-gray-500">
                              ({getTimezoneAbbreviation(userTimezone)})
                            </span>
                          </span>
                        </div>
                      );
                    })()}
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className={getCourseTypeColor(booking.course_type || '')}>
                        {getCourseTypeDisplay(booking.course_type || '', booking.subscription_type)}
                      </Badge>
                      <Badge className={getStatusColor(booking.status)}>
                        {booking.status}
                      </Badge>
                    </div>
                    {booking.notes && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-1">{booking.notes}</p>
                    )}
                  </div>
                </div>

                {booking.status === 'confirmed' && (booking.course_type === 'voiceCall' || booking.course_type === 'subscription') && (
                  <div className="flex items-center gap-1 shrink-0">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-sm">{getCallButtonState(booking).tooltip}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <Button 
                      variant="call"
                      size="sm"
                      onClick={() => handleStartCall(booking)}
                      disabled={isCreatingRoom || callInProgress || !getCallButtonState(booking).enabled}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      {isCreatingRoom || callInProgress ? 'Starting...' : 'Start Call'}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        
        {bookings.length > 5 && (
          <div className="text-center pt-2">
            <Button variant="ghost" size="sm" className="text-emu-blue">
              View All Appointments
            </Button>
          </div>
        )}
      </div>

    </>
  );
};

export default UpcomingAppointments;
